import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, frames, glyphs } from "../../../Styles";
import { FilterPopup, NewSelect, PageHeader, WaitAnimation } from "../../../Components";
import {
  ButtonTypes,
  NewFieldStyles,
  NewSelectHandles,
  ListData_ValueTypes,
  TableStyles_Types,
  NewInputHandles,
} from "../../../Definitions";
import { Pages } from "../../../Navigation/root";
import { useAppSelector } from "../../../Store";
import {
  AnimalInfo,
  Graph_QL,
  useAnimal,
  useLanControllers,
} from "../../../Services";

import { ColumnFiltersState, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../../Components/Tables/reactiveTableRenderer";
import globalFilter from "../../../Components/Tables/_Tables/useGlobalFilter";
import { CONTROLLER_LOG_AICO, LanControllerLogAicoParameters, LanControllerLogAicoResponse } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/controllerlogaico";
import { useLazyQuery } from "@apollo/client";
import { useColumnConstructor } from "../../../Components/Tables/_Tables/useColumnConstructor";
import NewButton from "../../../Components/BaseComponents/newButton";


// #endregion


const levelMap: Record<number, string> = {
  0: "Error",
  1: "Warning",
  2: "Info",
  3: "Debug",
};

function getLevelOptions() {
  return Object.entries(levelMap).map(([key, value]) => ({ label: value, id: +key }));
}
 
const typeMap: Record<number, string> = {
  1: "System",
  2: "Motor",
  3: "Animal",
};

function getTypeOptions() {
  return Object.entries(typeMap).map(([key, value]) => ({ label: value, id: +key }));
}

const accessorData = [
  { headerValue: "Time", accessor: "timestamp", type: ListData_ValueTypes.Date, sort: true, width: "200px" },
  { headerValue: "Level", accessor: "level", type: ListData_ValueTypes.List, sort: true, width: "120px", filter: true, options: getLevelOptions() },
  { headerValue: "Type", accessor: "type", type: ListData_ValueTypes.List, sort: true, width: "120px", filter: true, options: getTypeOptions() },
  { headerValue: "Message", accessor: "message", type: ListData_ValueTypes.String, sort: true},
  { headerValue: "Location", accessor: "location", type: ListData_ValueTypes.String, sort: true, width: "150px" },
  { headerValue: "Animal", accessor: "animalid", type: ListData_ValueTypes.String, sort: true, width: "150px" },
  { headerValue: "Controller", accessor: "controllername", type: ListData_ValueTypes.String, sort: true, width: "250px" },
]

const transformMessage = (msg: string) => {
  return msg
    .replace(/\bF(\d+)/g, "feedcurve $1") // Replace F<number> with feedcurve<number>
    .replace(/\bM(\d+)/g, "Milestone $1")    // Optional: Replace M<number> with Milestone<number>
    .replace(/\bD(\d+)/g, "Day $1");       // Optional: Replace D<number> with Day<number>
};

export default function Logbook() {
  const { t } = useTranslation();
  const [select, setSelect] = useState<string|number|null|undefined>();
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { call: getAnimalsInfo, data: animalsState } = useAnimal(connection);
  const animals = animalsState[domain];

  const [fetchLogs, { data: logResponse, loading, error, refetch}] = useLazyQuery<LanControllerLogAicoResponse, LanControllerLogAicoParameters>(CONTROLLER_LOG_AICO, { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true});
  const { call: getLanControllers, data: lanControllerData } = useLanControllers(connection);


  const logData = useMemo(() => {
    return logResponse?.controller.controllerLogsAico ?? [];
  }, [logResponse]);

  useEffect(() => {
    getLanControllers({ fetchAmount: 500 });
  }, []);

  const lanControllerOptions = useMemo(() => {
    if (lanControllerData && lanControllerData[domain]) {
      return [
        { label: "All Controllers", id: "all" },
        ...lanControllerData[domain].data?.map(lanController => ({
          label: lanController.name ?? "",
          id: lanController.lan_controller_id,
        })),
      ];
    }
    return [];
  }, [domain, lanControllerData]);


  const animalMapFromRedux = useMemo(() => {
    const map: Record<number, AnimalInfo> = {};
    animals?.data?.forEach(animal => {
      map[animal.animal_id] = animal;
    });
    return map;
  }, [animals]);

  useEffect(() => {
    if (!logData || logData.length === 0) return;

    const animalIds = Array.from(
      new Set(logData.map(log => log.fk_animals).filter(Boolean))
    );

    const missingIds = animalIds.filter(id => !animalMapFromRedux[id]);

    if (missingIds.length > 0) {
      getAnimalsInfo({ ids: missingIds });
    }
  }, [logData, animalMapFromRedux, getAnimalsInfo]);

  const allIds: number[] = lanControllerOptions
    .filter(opt => opt.id !== "all" && opt.id !== undefined)
    .map(opt => Number(opt.id));

  const input = {
    fetchAmount: 501,
    offset: 0,
    ids: select === "all" ? allIds : [select as number],
  };

  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef<NewInputHandles>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const tableData = useMemo(() => {
    return logData.map((log) => {
      const animalInfo = log.fk_animals ? animalMapFromRedux[log.fk_animals] : null;

      return {
        id: log.lan_controller_log_id,
        timestamp: new Date(log.timestamp * 1000),
        level: { label: levelMap[log.level], id: log.level },
        type: { label: typeMap[log.type], id: log.type },
        message: transformMessage(log.message),
        location: log.location_name ?? "",
        animalid: (
          <>
            {animalInfo?.animal_number && `Animal: ${animalInfo.animal_number}`}{" "}&nbsp;&nbsp; {animalInfo?.location_id && `Pen: ${animalInfo.location_id}`}
            <br />
            {animalInfo?.responder_number && `Responder: ${animalInfo.responder_number}`}
          </>
        ),
        controllername: (
          <>
            {log.controller_name && `Controller Name: ${log.controller_name}`} 
            <br />
            {log.device_name && `Device Name: ${log.device_name}`}
          </>
        ),
      };
    });
  }, [logData, animalMapFromRedux]);

  const columns = useColumnConstructor({
    accessorData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: false,
  });

  const pageSize = 500;

  const totalLogs = logResponse?.controller?.controllerLogsAico.length ?? 0;

  const totalPages = Math.ceil(totalLogs / pageSize);

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    manualPagination: true,
    pageCount: totalPages,  
    globalFilterFn: globalFilter,
    state: {
          globalFilter: globalFilterValue,
          columnFilters,
        },
    initialState: {
      sorting: [{ id: "timestamp", desc: true }],
      pagination: { pageSize },
    }
  });

  const ClickHandler = () => {
    if (!select) return;
    
    table.firstPage();

    fetchLogs({
      variables: {
        domain,
        input,
        filter: {},
      },
    });
  };

    const pageCount = useMemo(() => (
      loading ? <WaitAnimation color={colors.greyLightTertiary}/> :
      <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
        {`${t("page", { defaultValue: "page" })} ${ totalLogs === 0 ? 0 : table.getState().pagination.pageIndex + 1} ${t("of", { defaultValue: "of" })} ${totalPages}`}
      </p>
    ), [loading, t, table, totalLogs, totalPages]);
  

  return (
    <frames.Page_inner>
        <PageHeader
          topHeader={t(`menuHeaders.${Pages.logbook}`, {
            defaultValue: "logbook",
          })}
          subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
        />
      <frames._page_inner_content>
        <div style={{display: "grid", gridArea: "1/1/2/2", height: "100%", gridTemplateRows: "50px 1fr 40px",}}>
          <div style={{
            gridArea: "1/1/2/2",
            display: "flex",
            alignItems: "center",
            padding: "0px 20px",
            gap: "5px",
            height: "fit-content",
          }}>
            <NewSelect
              onChange={(value) => setSelect(value?.id)}
              options={lanControllerOptions}
              placeholder="Lan Controllers"
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greySecondary,
                fieldWidth: "180px",
              }}
            />
            <NewButton
              content={"Get Log"}
              buttonType={ButtonTypes.textButton}
              onClick={ClickHandler}
            />
            <div style={{marginLeft: "auto" }}>
              <FilterPopup
                accessorData={accessorData}
                table={table}
              />
            </div>
          </div>
          <div style={{ height: "100%", gridArea: "2/1/3/2", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", overflow: "hidden"}}>
            {ReactiveTable({ table, minRows: 24, tableType: TableStyles_Types.primaryTable, emptyText: "No logs available", renderFooter: false, loading })}
          </div>
          <div style={{ gridArea: "3/1/4/2", display: "flex", alignItems: "center", gap: "5px", padding: "5px 20px 0px 20px" }}>
            <NewButton
              content={""}
              buttonType={ButtonTypes.arrowButton}
              onClick={() => {
                if (!select) return;
                if (table.getCanPreviousPage()) {
                  refetch({
                    input: {
                      ...input,
                      offset: 500 * (table.getState().pagination.pageIndex - 1),
                    },
                  });
                  table.previousPage();
                }
              }}
              startEnhancer={glyphs.Arrow({ rotate: 180 })}
              overrides={{
                baseStyles: {backgroundColor: colors.greyPrimary},
                hoverStyles: {backgroundColor: colors.greySecondary},
              }}
              disabled={!(table.getCanPreviousPage())}
            />
            <NewButton
              content={""}
              buttonType={ButtonTypes.arrowButton}
              onClick={() => {
                if (!select) return;
                if (table.getCanNextPage() && (logData?.length && logData.length > 500)) {
                  
                  refetch({
                    input: {
                      ...input,
                      offset: 500 * (table.getState().pagination.pageIndex + 1),
                    },
                  });
                  table.nextPage();
                }
              }}
              startEnhancer={glyphs.Arrow({})}
              overrides={{
                baseStyles: {backgroundColor: colors.greyPrimary},
                hoverStyles: {backgroundColor: colors.greySecondary},
              }}
              disabled={!(table.getCanNextPage())}
            />
            <>{pageCount}</>
          </div>
        </div>
      </frames._page_inner_content>
    </frames.Page_inner>
  );
}
